import { useCallback, useEffect, useState, useRef } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { GridSortModel } from '@mui/x-data-grid';
import { debounce } from 'ts-debounce';
import { useAdminCompaniesService } from 'src/modules/companies/services/AdminCompaniesService';
import { AdminCompany } from 'src/modules/companies/types';
import { handleDownload } from 'src/helpers/helpers';
import { useReportsService } from '../services/ReportsService';
import { MessageErrorReportDetail } from '../types';

export const enum TimePeriod {
  LAST7DAYS = 'last7days',
  LAST30DAYS = 'last30days',
  LAST90DAYS = 'last90days',
  CUSTOM = 'custom',
}

export type GroupBy = 'application' | 'company' | 'error_code';

export const useReportsViewController = () => {
  const {
    messageErrorReports,
    messageErrorSummaryReports,
    filter: reportsFilter,
    loading: isPageLoading,
    loadReports,
    loadMessageErrorSummaryReports: loadSummaryReports,
    downloadMessageErrorReports,
    changeReportsFilter,
  } = useReportsService();

  const { list: companyList, getCompanies } = useAdminCompaniesService();

  const [timePeriod, setTimePeriod] = useState<TimePeriod>(TimePeriod.LAST30DAYS);
  const [startDate, setStartDate] = useState<Dayjs | null>(
    dayjs().subtract(30, 'days').startOf('day'),
  );
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs().endOf('day'));
  const [selectedCompany, setSelectedCompany] = useState<AdminCompany | null>(null);
  const [groupBy, setGroupBy] = useState<GroupBy>('company');
  const [sortConfig, setSortConfig] = useState<{ field: string; direction: 'asc' | 'desc' } | null>(
    null,
  );
  const [searchQuery, setSearchQuery] = useState<string>('');
  const latestFilterRef = useRef(reportsFilter);

  useEffect(() => {
    latestFilterRef.current = reportsFilter;
  }, [reportsFilter]);

  useEffect(() => {
    setSearchQuery(reportsFilter.query || '');
  }, [reportsFilter.query]);

  // Sync local date state with Redux filter state
  useEffect(() => {
    setStartDate(dayjs(reportsFilter.startDateInterval));
    setEndDate(dayjs(reportsFilter.endDateInterval));
  }, [reportsFilter.startDateInterval, reportsFilter.endDateInterval]);

  // Load companies on mount
  useEffect(() => {
    getCompanies();
  }, [getCompanies]);

  // Load initial data on mount with default groupBy
  useEffect(() => {
    const startDate = dayjs().subtract(30, 'days').startOf('day');
    const endDate = dayjs().endOf('day');
    const startDateString = startDate.toISOString();
    const endDateString = endDate.toISOString();
    changeReportsFilter({
      groupBy: 'company' as const,
      startDateInterval: startDateString,
      endDateInterval: endDateString,
    });
    loadReports({
      groupBy: 'company' as const,
      startDateInterval: startDateString,
      endDateInterval: endDateString,
    });
    loadSummaryReports({
      startDateInterval: startDateString,
      endDateInterval: endDateString,
    });
  }, []);

  const performSearch = useCallback(
    async (query?: string) => {
      try {
        const newFilter: Record<string, unknown> = { ...latestFilterRef.current };
        if (query !== undefined) {
          if (query.trim()) {
            newFilter.query = query.trim();
          } else {
            delete newFilter.query;
          }
          changeReportsFilter(newFilter);
        }
        await loadReports(newFilter);
      } catch (e) {
        console.log(e);
      }
    },
    [changeReportsFilter, loadReports],
  );

  // Debounced search: recreate when performSearch changes, cancel on cleanup
  const debouncedSearchRef = useRef<ReturnType<typeof debounce> | null>(null);
  useEffect(() => {
    const debounced = debounce((query: string) => {
      void performSearch(query);
    }, 500);
    debouncedSearchRef.current = debounced;
    return () => {
      // ts-debounce exposes cancel()
      (debounced as { cancel?: () => void }).cancel?.();
    };
  }, [performSearch]);

  const changeCompanyFilter = useCallback(
    (companyId: string) => {
      const newFilter = { ...reportsFilter, companyId: companyId || undefined };
      changeReportsFilter(newFilter);
      loadReports(newFilter);
    },
    [changeReportsFilter, reportsFilter, loadReports],
  );

  const changeDateFilter = useCallback(
    (startDateInterval?: Dayjs, endDateInterval?: Dayjs) => {
      const newFilter = {
        ...reportsFilter,
        startDateInterval: startDateInterval?.toISOString(),
        endDateInterval: endDateInterval?.toISOString()
      };
      changeReportsFilter(newFilter);
      loadReports(newFilter);
      if (startDateInterval && endDateInterval) {
        loadSummaryReports({
          startDateInterval: startDateInterval.toISOString(),
          endDateInterval: endDateInterval.toISOString()
        });
      }
    },
    [changeReportsFilter, reportsFilter, loadReports, loadSummaryReports],
  );

  const changeGroupByFilter = useCallback(
    (newGroupBy: GroupBy) => {
      const newFilter = { ...reportsFilter, groupBy: newGroupBy };
      changeReportsFilter(newFilter);
      loadReports(newFilter);
    },
    [changeReportsFilter, reportsFilter, loadReports],
  );

  const changeSortFilter = useCallback(
    (newSortConfig: { field: string; direction: 'asc' | 'desc' } | null) => {
      const newFilter = { ...reportsFilter };
      if (newSortConfig) {
        newFilter.sort = `${newSortConfig.field}_${newSortConfig.direction}`;
      } else {
        delete newFilter.sort;
      }
      changeReportsFilter(newFilter);
      loadReports(newFilter);
    },
    [changeReportsFilter, reportsFilter, loadReports],
  );

  const changeReportsSearchFilter = useCallback((query: string) => {
    setSearchQuery(query);
    debouncedSearchRef.current?.(query);
  }, []);

  const handleTimePeriodChange = useCallback(
    (period: TimePeriod) => {
      setTimePeriod(period);
      let newStartDate: Dayjs;
      const newEndDate = dayjs().endOf('day');

      switch (period) {
        case TimePeriod.LAST7DAYS:
          newStartDate = dayjs().subtract(7, 'days').startOf('day');
          break;
        case TimePeriod.LAST30DAYS:
          newStartDate = dayjs().subtract(30, 'days').startOf('day');
          break;
        case TimePeriod.LAST90DAYS:
          newStartDate = dayjs().subtract(90, 'days').startOf('day');
          break;
        default:
          return;
      }

      setStartDate(newStartDate);
      setEndDate(newEndDate);
      changeDateFilter(newStartDate, newEndDate);
    },
    [changeDateFilter],
  );

  const handleStartDateChange = useCallback(
    (date: Dayjs | null) => {
      setStartDate(date);
      if (date && endDate) {
        changeDateFilter(date, endDate);
      }
    },
    [endDate, changeDateFilter],
  );

  const handleEndDateChange = useCallback(
    (date: Dayjs | null) => {
      setEndDate(date);
      if (startDate && date) {
        changeDateFilter(startDate, date);
      }
    },
    [startDate, changeDateFilter],
  );

  const handleCompanyChange = useCallback(
    (company: AdminCompany | null) => {
      setSelectedCompany(company);
      changeCompanyFilter(company ? company.id : '');
    },
    [changeCompanyFilter],
  );

  const handleGroupByChange = useCallback(
    (newGroupBy: GroupBy) => {
      setGroupBy(newGroupBy);
      changeGroupByFilter(newGroupBy);
    },
    [changeGroupByFilter],
  );

  const handleSortChange = useCallback(
    (sortModel: GridSortModel) => {
      if (sortModel.length > 0) {
        const sortItem = sortModel[0];
        // Map GridData column fields back to API sort fields
        let apiField = sortItem.field;
        if (sortItem.field === 'companyName') apiField = 'company_name';
        if (sortItem.field === 'applicationName') apiField = 'application_name';
        if (sortItem.field === 'errorCode') apiField = 'error_code';
        if (sortItem.field === 'errorCount') apiField = 'error_count';
        if (sortItem.field === 'totalMessages') apiField = 'total_messages';
        if (sortItem.field === 'timePeriod') apiField = 'interval_start';

        const newSortConfig = {
          field: apiField,
          direction: (sortItem.sort as 'asc' | 'desc') ?? 'asc',
        };
        setSortConfig(newSortConfig);
        changeSortFilter(newSortConfig);
      } else {
        setSortConfig(null);
        changeSortFilter(null);
      }
    },
    [changeSortFilter],
  );

  const handleSearchChange = useCallback(
    (query: string) => {
      setSearchQuery(query);
      changeReportsSearchFilter(query);
    },
    [changeReportsSearchFilter],
  );

  const handleDownloadMessageErrorReport = useCallback(
    async (row: MessageErrorReportDetail) => {
      if (!startDate || !endDate) {
        return;
      }

      try {
        const response = await downloadMessageErrorReports({
          applicationId: row.application?.id,
          companyId: row.company?.id,
          errorCodes: row.errorCode ? [row.errorCode] : undefined,
          startDateInterval: startDate,
          endDateInterval: endDate,
        });
        handleDownload(response);
      } catch (e) {
        console.log(e);
      }
    },
    [downloadMessageErrorReports, startDate, endDate],
  );

  return {
    // Data
    messageErrorReports,
    messageErrorSummaryReports,
    companyList,
    isPageLoading,

    // Filter state
    timePeriod,
    startDate,
    endDate,
    selectedCompany,
    groupBy,
    sortConfig,
    searchQuery,

    // Actions
    handleTimePeriodChange,
    handleStartDateChange,
    handleEndDateChange,
    handleCompanyChange,
    handleGroupByChange,
    handleSortChange,
    handleSearchChange,
    handleDownloadMessageErrorReport,
    changeReportsSearchFilter,
  };
};
